import type {
  AvailableShare,
  ShareGrant,
  ShareRemoved,
} from "@aasaan-share/protocol";
import { GallerySession } from "./gallery-session.ts";

export interface ViewerShare {
  readonly access: "view" | "download";
  readonly canContribute: boolean;
  readonly capabilities: { readonly videoDownloadV1: boolean };
  readonly collaborators?: AvailableShare["collaborators"];
  readonly expiresAt: string;
  readonly gallery: GallerySession;
  readonly label: string;
  readonly originalMediaCount?: number;
  readonly ownerAvatarUrl?: string;
  readonly ownerEmail?: string;
  readonly ownerUsername?: string;
  readonly revision: string;
  readonly shareId: string;
  readonly viewerRecipientId?: string;
}

export interface CatalogueChange {
  readonly removedSelected: boolean;
  readonly selectedShareId: string | null;
  readonly updatedShareIds: readonly string[];
}

export class ShareCatalogue {
  readonly #shares = new Map<string, ViewerShare>();
  #selectedShareId: string | null = null;

  get selectedShareId(): string | null {
    return this.#selectedShareId;
  }

  get selected(): ViewerShare | null {
    return this.#selectedShareId
      ? this.#shares.get(this.#selectedShareId) ?? null
      : null;
  }

  get size(): number {
    return this.#shares.size;
  }

  values(): readonly ViewerShare[] {
    return [...this.#shares.values()];
  }

  get(shareId: string): ViewerShare | undefined {
    return this.#shares.get(shareId);
  }

  reconcile(
    available: readonly AvailableShare[],
    grants: readonly ShareGrant[],
  ): CatalogueChange {
    const grantsByShare = new Map(grants.map((grant) => [grant.shareId, grant]));
    const previousSelected = this.#selectedShareId;
    const liveIds = new Set<string>();
    const updatedShareIds: string[] = [];

    for (const share of available) {
      const grant = grantsByShare.get(share.shareId);
      if (!grant) continue;
      liveIds.add(share.shareId);
      const previous = this.#shares.get(share.shareId);
      if (previous && previous.revision !== share.revision) {
        updatedShareIds.push(share.shareId);
      }
      this.#shares.set(share.shareId, {
        access: grant.access,
        canContribute: grant.canContribute === true,
        capabilities: {
          videoDownloadV1: grant.access === "download"
            && grant.capabilities.videoDownloadV1 === true,
        },
        collaborators: share.collaborators,
        expiresAt: grant.expiresAt,
        gallery: previous?.gallery ?? new GallerySession(share.shareId),
        label: share.label,
        originalMediaCount: share.originalMediaCount,
        ownerAvatarUrl: share.ownerAvatarUrl,
        ownerEmail: share.ownerEmail,
        ownerUsername: share.ownerUsername,
        revision: share.revision,
        shareId: share.shareId,
        viewerRecipientId: grant.viewerRecipientId,
      });
    }

    for (const shareId of [...this.#shares.keys()]) {
      if (!liveIds.has(shareId)) this.remove(shareId);
    }

    if (!this.#selectedShareId || !this.#shares.has(this.#selectedShareId)) {
      this.#selectedShareId = this.#shares.keys().next().value ?? null;
    }

    return {
      removedSelected: previousSelected !== null
        && previousSelected !== this.#selectedShareId
        && !this.#shares.has(previousSelected),
      selectedShareId: this.#selectedShareId,
      updatedShareIds,
    };
  }

  select(shareId: string): ViewerShare {
    const share = this.#shares.get(shareId);
    if (!share) throw new Error("This share is not available");
    this.#selectedShareId = shareId;
    return share;
  }

  remove(
    shareId: string,
    _reason?: ShareRemoved["reason"],
  ): CatalogueChange {
    const removedSelected = this.#selectedShareId === shareId;
    const share = this.#shares.get(shareId);
    share?.gallery.dispose();
    this.#shares.delete(shareId);
    if (removedSelected) {
      this.#selectedShareId = this.#shares.keys().next().value ?? null;
    }
    return {
      removedSelected,
      selectedShareId: this.#selectedShareId,
      updatedShareIds: [],
    };
  }

  updateGrant(grant: ShareGrant): void {
    const share = this.#shares.get(grant.shareId);
    if (!share) return;
    this.#shares.set(grant.shareId, {
      ...share,
      access: grant.access,
      canContribute: grant.canContribute === true,
      capabilities: {
        videoDownloadV1: grant.access === "download"
          && grant.capabilities.videoDownloadV1 === true,
      },
      expiresAt: grant.expiresAt,
      viewerRecipientId: grant.viewerRecipientId,
    });
    if (!canKeepVideo(share, grant)) share.gallery.removeVideos();
  }

  dispose(): void {
    for (const share of this.#shares.values()) share.gallery.dispose();
    this.#shares.clear();
    this.#selectedShareId = null;
  }
}

function canKeepVideo(share: ViewerShare, grant: ShareGrant): boolean {
  return share.capabilities.videoDownloadV1
    && grant.access === "download"
    && grant.capabilities.videoDownloadV1;
}
